import React from 'react';
import type { OptimizationJobRunnerProgress } from '../../types/optimizationJobRunnerProgress';
import { resolveOptimizationJobRunnerProgressDisplay } from '../../utils/optimizationJobRunnerProgress';
import '../training/FineTuningJobProgress.css';

export interface OptimizationJobProgressBarProps {
  percent: number;
  message?: string | null;
  meta?: string | null;
  runnerProgress?: OptimizationJobRunnerProgress | null;
  job?: unknown;
  title?: string;
  accentColor?: string;
  compact?: boolean;
  className?: string;
}

const OptimizationJobProgressBar: React.FC<OptimizationJobProgressBarProps> = ({
  percent,
  message,
  meta,
  runnerProgress,
  job,
  title = 'Optimization Progress',
  accentColor = '#FF8300',
  compact = false,
  className = '',
}) => {
  const clamped = Math.max(0, Math.min(100, percent));
  const widthPercent = clamped > 0 ? Math.max(clamped, 2) : 0;
  const resolved =
    message != null || meta != null
      ? { message: message ?? null, meta: meta ?? null }
      : resolveOptimizationJobRunnerProgressDisplay(job ?? runnerProgress, runnerProgress);
  const { message: caption, meta: captionMeta } = resolved;

  return (
    <div
      className={['ft-job-progress', compact ? 'ft-job-progress--compact' : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="ft-job-progress__head">
        <span className="ft-job-progress__title">{title}</span>
        <span className="ft-job-progress__percent">{Math.round(clamped)}%</span>
      </div>
      <div
        className="ft-job-progress__track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(clamped)}
        aria-label={`${title} ${Math.round(clamped)} percent`}
      >
        <div
          className="ft-job-progress__fill ft-job-progress__fill--running"
          style={{ width: `${widthPercent}%`, background: accentColor }}
        />
      </div>
      {caption ? <div className="ft-job-progress__label">{caption}</div> : null}
      {captionMeta ? (
        <div className="ft-job-progress__label" style={{ opacity: 0.85, marginTop: caption ? 4 : undefined }}>
          {captionMeta}
        </div>
      ) : null}
    </div>
  );
};

export default OptimizationJobProgressBar;
